/* ==========================================================================
   3afret Electronics — product.js
   Variant picker, media gallery, zoom lightbox, sticky add-to-cart, quick view.
   Variant data is pre-rendered by Liquid (including formatted money), so
   switching a variant needs no network request.
   ========================================================================== */
(function () {
  'use strict';

  const Theme = (window.Theme = window.Theme || {});

  /* ------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.url = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      const dataEl = this.querySelector('[data-variant-data]');
      if (!dataEl) return;
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }

      this.scope = document.getElementById(this.dataset.scope) || document;
      this.addEventListener('change', (event) => this.onChange(event));
      this.updateAvailability();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((input) =>
        input.tagName === 'SELECT' ? input.value : input.value
      );
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
    }

    onChange(event) {
      let options = this.selectedOptions;
      let variant = this.findVariant(options);

      /* The chosen value has no variant with the other selections (e.g. a colour sold
         only in 8GB/256GB while 4GB/128GB is selected): jump to the nearest variant
         that has it instead of leaving the customer on a dead button. */
      if (!variant && event && event.target) {
        const group = Theme.closest(event.target, '[data-option-index]');
        if (group) {
          const fallback = this.nearestVariant(parseInt(group.dataset.optionIndex, 10), event.target.value, options);
          if (fallback) {
            this.applyVariant(fallback);
            options = this.selectedOptions;
            variant = this.findVariant(options);
          }
        }
      }

      this.updateOptionLabels();
      this.updateAvailability();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.current = variant;
      this.updateIdInput(variant);
      this.updatePrice(variant);
      this.updateMeta(variant);
      this.updateButtons(variant);
      this.updateMedia(variant);
      this.updateHistory(variant);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateOptionLabels() {
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const output = group.querySelector('[data-option-selected]');
        if (checked && output) output.textContent = checked.value;
      });
    }

    /* Cross out option values that produce no purchasable combination. */
    updateAvailability() {
      const selected = this.selectedOptions;
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const exists = this.variants.some((variant) =>
            variant.options.every((value, i) => (i === index ? value === input.value : value === candidate[i]))
          );
          const purchasable = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every((value, i) => (i === index ? value === input.value : value === candidate[i]))
          );
          const visual = input.nextElementSibling;
          if (!visual) return;
          /* Never disable: a value that has no variant with the current selections is
             still clickable and onChange() switches the other options to match. */
          /* faded  = this value does not exist with the current selections (clicking it switches them)
             crossed = the combination exists but is sold out */
          visual.classList.toggle('variant-pill--faded', !exists);
          visual.classList.toggle('variant-swatch--faded', !exists);
          visual.classList.toggle('variant-pill--unavailable', exists && !purchasable);
          visual.classList.toggle('variant-swatch--unavailable', exists && !purchasable);
          input.disabled = false;
        });
      });
    }

    /* Variant carrying `value` at option `index` that is closest to the current
       selections; in-stock variants first. */
    nearestVariant(index, value, current) {
      const overlap = (v) => v.options.reduce((n, o, i) => n + (i !== index && o === current[i] ? 1 : 0), 0);
      const rank = (v) => (v.available ? 100 : 0) + overlap(v);
      return this.variants
        .filter((v) => v.options[index] === value)
        .sort((a, b) => rank(b) - rank(a))[0] || null;
    }

    applyVariant(variant) {
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const i = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === variant.options[i];
        });
        const select = group.querySelector('select');
        if (select) select.value = variant.options[i];
      });
    }

    updateIdInput(variant) {
      this.scope.querySelectorAll('[data-variant-id-input]').forEach((input) => {
        input.value = variant.id;
      });
    }

    updatePrice(variant) {
      this.scope.querySelectorAll('[data-price-block]').forEach((block) => {
        const onSale = Boolean(variant.compare_at);
        block.classList.toggle('price--on-sale', onSale);

        const current = block.querySelector('[data-price-current]');
        if (current) current.innerHTML = variant.price;

        const compare = block.querySelector('[data-price-compare]');
        if (compare) {
          compare.innerHTML = variant.compare_at || '';
          compare.hidden = !onSale;
        }

        const discount = block.querySelector('[data-price-discount]');
        if (discount) {
          discount.textContent = variant.discount ? `-${variant.discount}%` : '';
          discount.hidden = !variant.discount;
        }
      });

      this.scope.querySelectorAll('[data-installment-amount]').forEach((el) => {
        el.textContent = variant.installment || '';
      });
    }

    updateMeta(variant) {
      this.scope.querySelectorAll('[data-variant-sku]').forEach((el) => {
        el.textContent = variant.sku || '—';
        const row = el.closest('[data-variant-sku-row]');
        if (row) row.hidden = !variant.sku;
      });

      this.scope.querySelectorAll('[data-variant-availability]').forEach((el) => {
        el.className = 'product-availability product-availability--' + variant.stock_state;
        el.textContent = variant.stock_label;
      });

      this.scope.querySelectorAll('[data-variant-badges]').forEach((el) => {
        el.innerHTML = variant.badges || '';
      });
    }

    updateButtons(variant) {
      this.scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        const label = button.querySelector('span') || button;
        button.disabled = !variant.available;
        label.textContent = variant.available ? button.dataset.labelAvailable || 'Add to Cart' : variant.sold_out_label;
      });
      this.scope.querySelectorAll('[data-buy-now]').forEach((button) => {
        button.disabled = !variant.available;
      });
    }

    setUnavailable() {
      this.scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        const label = button.querySelector('span') || button;
        button.disabled = true;
        label.textContent = button.dataset.labelUnavailable || 'Unavailable';
      });
      this.scope.querySelectorAll('[data-buy-now]').forEach((button) => {
        button.disabled = true;
      });
    }

    updateMedia(variant) {
      if (!variant.media_id) return;
      const gallery = this.scope.querySelector('media-gallery') || document.querySelector('media-gallery');
      if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(variant.media_id);
    }

    updateHistory(variant) {
      if (!this.updateUrl || !this.url) return;
      const url = new URL(this.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     Media gallery
     ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      if (!this.viewport) return;
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.galleryThumb, true));
      });

      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.viewport.addEventListener(
        'scroll',
        Theme.debounce(() => this.syncFromScroll(), 80),
        { passive: true }
      );

      if (this.dataset.zoom === 'true') this.initZoom();
      this.syncFromScroll();
    }

    get slides() {
      return Array.from(this.viewport.querySelectorAll('[data-gallery-slide]'));
    }

    get activeIndex() {
      const slides = this.slides;
      const pos = Math.abs(this.viewport.scrollLeft);
      const width = this.viewport.clientWidth || 1;
      return Math.min(slides.length - 1, Math.max(0, Math.round(pos / width)));
    }

    step(direction) {
      const slides = this.slides;
      const next = Math.min(slides.length - 1, Math.max(0, this.activeIndex + direction));
      this.scrollToIndex(next);
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
      this.setActiveThumb(index);
    }

    showMedia(mediaId, fromThumb) {
      const slides = this.slides;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (fromThumb) this.scrollToIndex(index);
      else {
        // Variant change: jump without smooth scrolling so it feels instant.
        const slide = slides[index];
        this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'auto' });
        this.setActiveThumb(index);
      }
      this.pauseVideos(index);
    }

    setActiveThumb(index) {
      const slides = this.slides;
      const mediaId = slides[index] ? slides[index].dataset.mediaId : null;
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.galleryThumb === mediaId;
        thumb.setAttribute('aria-current', String(active));
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }

    syncFromScroll() {
      this.setActiveThumb(this.activeIndex);
    }

    pauseVideos(exceptIndex) {
      this.slides.forEach((slide, i) => {
        if (i === exceptIndex) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    initZoom() {
      this.addEventListener('click', (event) => {
        const trigger = Theme.closest(event.target, '[data-gallery-zoom]');
        if (!trigger) return;
        event.preventDefault();
        const slide = this.slides[this.activeIndex];
        const img = slide ? slide.querySelector('img') : null;
        if (img) Lightbox.open(img.currentSrc || img.src, img.alt);
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------
     Lightbox
     ------------------------------------------------------------------ */
  const Lightbox = {
    open(src, alt) {
      let box = document.getElementById('lightbox');
      if (!box) {
        box = document.createElement('div');
        box.id = 'lightbox';
        box.className = 'lightbox';
        box.setAttribute('role', 'dialog');
        box.setAttribute('aria-modal', 'true');
        box.setAttribute('aria-label', 'Product image');
        box.innerHTML =
          '<button type="button" class="lightbox__close" data-lightbox-close aria-label="Close">' +
          '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
          '</button><img alt="">';
        document.body.appendChild(box);
        box.addEventListener('click', (event) => {
          if (event.target === box || Theme.closest(event.target, '[data-lightbox-close]')) this.close();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
      }
      const img = box.querySelector('img');
      img.src = src;
      img.alt = alt || '';
      box.classList.add('is-open');
      Theme.lockScroll();
      this.release = Theme.trapFocus(box, document.activeElement);
    },

    close() {
      const box = document.getElementById('lightbox');
      if (!box || !box.classList.contains('is-open')) return;
      box.classList.remove('is-open');
      Theme.unlockScroll();
      if (this.release) {
        this.release();
        this.release = null;
      }
    }
  };
  Theme.lightbox = Lightbox;

  /* ------------------------------------------------------------------
     Sticky add to cart (mobile)
     ------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      // data-scope limits the bar to a container (the quick view sheet): it watches,
      // listens and submits inside that container and leaves the page-level bar alone.
      this.scope = this.dataset.scope ? this.closest(this.dataset.scope) : null;
      const searchRoot = this.scope || document;
      this.watch = searchRoot.querySelector(this.dataset.watch || '[data-atc-anchor]');
      if (!this.watch || !('IntersectionObserver' in window)) return;
      const scrollRoot = this.scope ? this.scope.closest('.modal__scroll') : null;

      // Show only once the real Add to Cart has scrolled out of view (above the top edge).
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const edge = entry.rootBounds ? entry.rootBounds.top : 0;
            // Page: only once the button has scrolled past the top. Sheet (scoped): whenever
            // the button is out of view, including below the fold when the sheet opens.
            const show = this.scope
              ? !entry.isIntersecting
              : !entry.isIntersecting && entry.boundingClientRect.top < edge;
            this.classList.toggle('is-visible', show);
            // Lets other bottom bars (the compare bar) stack above the page-level bar.
            if (!this.scope) document.body.style.setProperty('--sticky-atc-h', show ? `${this.offsetHeight}px` : '0px');
          });
        },
        { threshold: 0, root: scrollRoot }
      );
      this.observer.observe(this.watch);
      if (!this.scope) document.body.classList.add('has-sticky-atc');

      // Keep the sticky price in sync with the selected variant.
      searchRoot.addEventListener('variant:change', (event) => {
        // The page-level bar ignores variant changes made inside a quick view.
        if (!this.scope && event.target.closest && event.target.closest('[data-quick-view-body]')) return;
        const variant = event.detail.variant;
        const price = this.querySelector('[data-sticky-price]');
        if (price) price.innerHTML = variant.price + (variant.compare_at ? ` <s>${variant.compare_at}</s>` : '');
        const button = this.querySelector('[data-sticky-atc-button]');
        if (button) {
          button.disabled = !variant.available;
          button.textContent = variant.available ? Theme.strings.addToCart : variant.sold_out_label;
        }
      });

      const trigger = this.querySelector('[data-sticky-atc-button]');
      if (trigger) {
        trigger.addEventListener('click', () => {
          const form = (this.scope || document).querySelector('product-form form');
          if (!form) return;
          const submitButton = form.querySelector('[data-add-to-cart]');
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton);
          else if (submitButton) submitButton.click();
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.scope) return;
      document.body.classList.remove('has-sticky-atc');
      document.body.style.removeProperty('--sticky-atc-h');
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------
     Quick view
     ------------------------------------------------------------------ */
  async function openQuickView(trigger, event) {
    // Safari can report a non-Element target for clicks on SVG/text nodes.
    // Resolve through the parent element before using closest().
    event.preventDefault();

    const url = trigger.dataset.quickView;
    if (!url) return;

    const modal = document.getElementById('quick-view-modal');
    if (!modal) return;
    const body = modal.querySelector('[data-quick-view-body]');
    body.innerHTML = '<div class="modal__loading" role="status"><span class="visually-hidden">Loading</span></div>';
    Theme.overlays.show('quick-view-modal', trigger);

    try {
      const separator = url.indexOf('?') > -1 ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=quick-view`);
      const text = await response.text();
      const parsedHtml = Theme.getSectionHTML(text, '#shopify-section-quick-view');
      const html = parsedHtml === null ? text : parsedHtml;
      body.innerHTML = html;
    } catch (error) {
      body.innerHTML = '<p class="text-center text-muted">Could not load this product. Please try again.</p>';
    }
  }

  // Mobile Safari can occasionally omit the synthetic click after a touch
  // interaction, especially after a dynamically replaced quick-view body.
  // Handle the first pointer/touch activation directly and suppress the
  // duplicate click that Safari may emit afterwards.
  function handleQuickViewActivation(event) {
    const trigger = Theme.closest(event.target, '[data-quick-view]');
    if (!trigger) return;

    const now = Date.now();
    const previous = Number(trigger.getAttribute('data-qv-last-activation') || 0);
    if (now - previous < 600) {
      if (event.type === 'click') event.preventDefault();
      return;
    }

    trigger.setAttribute('data-qv-last-activation', String(now));
    event.preventDefault();
    if (event.type !== 'click') event.stopPropagation();
    openQuickView(trigger, event);
  }

  document.addEventListener('pointerup', handleQuickViewActivation, false);
  document.addEventListener('touchend', handleQuickViewActivation, false);
  document.addEventListener('click', handleQuickViewActivation, false);
})();
